import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';

const WebsiteNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "L'application", path: '/site/application' },
    { name: 'Rénovation énergétique', path: '/site/renovation' },
    { name: "L'équipe", path: '/site/equipe' },
    { name: 'Contact', path: '/site/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="website-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-slate-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/site" className="flex items-center gap-3" data-testid="navbar-logo">
            <img
              src="https://www.syndicappli.fr/wp-content/uploads/2023/04/cropped-Logo-Syndicappli_WHITE_2021_SMALL.png"
              alt="SyndicAppli"
              className={`h-10 transition-all duration-300 ${
                isScrolled ? 'filter-none' : ''
              }`}
              style={isScrolled ? { filter: 'brightness(0) saturate(100%) invert(51%) sepia(95%) saturate(401%) hue-rotate(121deg) brightness(95%) contrast(101%)' } : {}}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'bg-[#00D3A8] text-white'
                    : isScrolled
                    ? 'text-slate-600 hover:text-[#00D3A8] hover:bg-[#00D3A8]/5'
                    : 'text-white/90 hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                isScrolled
                  ? 'text-slate-600 hover:text-[#00D3A8] hover:bg-[#00D3A8]/5'
                  : 'text-white/90 hover:bg-white/10'
              }`}
            >
              Se connecter
            </Link>
            <Link
              to="/site/contact"
              data-testid="nav-demo-btn"
              className="px-6 py-2.5 bg-gradient-to-r from-[#00D3A8] to-[#00B894] text-white rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-[#00D3A8]/30 transition-all duration-200 flex items-center gap-2"
            >
              Réserver une démo
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-slate-600' : 'text-white'
            }`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white rounded-2xl shadow-xl mt-2 p-4 animate-in slide-in-from-top-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#00D3A8]/10 text-[#00D3A8]'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <hr className="my-3 border-slate-100" />
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Se connecter
            </Link>
            <Link
              to="/site/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="block mt-2 px-4 py-3 bg-gradient-to-r from-[#00D3A8] to-[#00B894] text-white rounded-xl text-sm font-semibold text-center"
            >
              Réserver une démo
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export { WebsiteNavbar };
